import { Options, Vue } from 'vue-class-component';
import AxiosService from '@/views/calculators/services/axiosService';
import Autocomplete from 'vue3-autocomplete';
import { modalWithExit, togglePopup } from '@/views/calculators/services/AlertService';
import store from '@/store/store';
import { defaultConfig } from '@/cms/api/ApiConfig';

type DarAddressResult = {
	type: string;
	id: string;
	titel: string;
	vejnavn: string;
	postnr: string;
	postdistrikt: string;
	antalHusnumre?: number;
};

type DarAddressResponse = {
	status: string;
	fund?: Array<DarAddressResult>;
};

@Options({
	name: 'AddressSearchComponent',
	components: {},
	props: {
		label: String,
		autocompleteId: String,
		defaultValue: { type: String, default: null },
		isOnlyZipSearch: { type: Boolean, default: false },
		isValid: { type: Boolean, default: false },
		placeholder: String,
		readonlyValue: { type: String, default: null },
		visibleError: { type: Boolean, default: false },
	},
})
export default class AddressSearchComponent extends Vue {
	label: string;
	autocompleteId?: string;
	defaultValue?: string; // if model has value
	isOnlyZipSearch?: boolean; // search only zipcode/city
	isValid?: boolean; // valid search result
	placeholder: string;
	readonlyValue?: string;
	visibleError: boolean;

	public dirty = true;
	private autocomplete: Autocomplete;
	public searching = false;
	public results: Array<DarAddressResult> = [];
	private doBlur = true;

	// https://dawadocs.dataforsyningen.dk/dok/api/adgangsadresse#autocomplete
	private searchUrl = '';

	// klk look into //api.dataforsyningen.dk/adresser?q= - different format and up to 1000 results
	public axiosService = new AxiosService();

	public mounted() {
		const calculatorProxyBase = `${defaultConfig.baseUrl}${store.state.openServiceCalculatorUrl}`;
		this.searchUrl = `${calculatorProxyBase}/adresser/soeg?tekst=`;
		this.axiosService.setWithCredentials(false);
	}

	get inputValid() {
		return !this.dirty && this.isValid && !this.searching && !this.readonlyValue;
	}
	get searchReady() {
		return !this.readonlyValue && !this.searching && (!this.isValid || this.dirty) && !this.showError;
	}

	get showError() {
		return this.visibleError && !this.readonlyValue && !this.searching && !this.isValid;
	}

	public setAutoCompleteField(input) {
		this.autocomplete = input;
		if (this.defaultValue) {
			this.autocomplete.setText(this.defaultValue);
			this.dirty = false;
		}
	}

	public displayItem(item: DarAddressResult) {
		return this.getDisplayText(item);
	}

	public async onFocus(): Promise<any> {
		this.dirty = true;
		await this.search(this.autocomplete.searchText);
	}

	public async search(input): Promise<any> {
		this.dirty = true;
		this.doBlur = true;

		this.results = [];
		if (input?.length < 2) {
			this.$emit('AddressSearch', {
				matchText: undefined,
			});
			this.searching = false;
			return;
		}
		this.searching = true;
		const url = `${this.searchUrl}${encodeURI(input)}`;
		const response = await this.axiosService.getRetry(url);
		if (response?.status !== 200) {
			this.$emit('AddressSearch', {
				matchText: undefined,
			});
			this.searching = false;
			this.addressSearchErrorPopUp();
			return;
		}

		const responseData = response?.data as DarAddressResponse;
		const responseResults = responseData?.fund ?? [];
		const idResults = responseResults.filter((item) => this.hasResultId(item));

		if (idResults.length > 0) {
			this.results = idResults;
		} else {
			this.$emit('AddressSearch', {
				matchText: undefined,
			});
		}
		this.searching = false;
	}

	public handleSubmit(result: DarAddressResult) {
		this.autocomplete.setText(this.getDisplayText(result));
		this.emit(result);
	}

	public blur(evt) {
		if (this.doBlur && this.results.length > 0) {
			if (this.autocomplete.searchText !== this.getDisplayText(this.results[0])) {
				this.handleSubmit(this.results[0]);
			}
		}
		this.doBlur = false;
	}

	private hasResultId(result: DarAddressResult): boolean {
		return !!result?.id;
	}

	private getDisplayText(result: DarAddressResult): string {
		if (!result) {
			return '';
		}

		return result.titel;
	}

	private emit(result: DarAddressResult) {
		if (result) {
			let res: any;
			if (this.isOnlyZipSearch) {
				// Postnr
				res = {
					matchText: this.getDisplayText(result),
					zipCode: result.postnr,
					zipName: result.postdistrikt,
					id: result.id,
				};
			} else {
				// adresse
				res = {
					matchText: this.getDisplayText(result),
					id: result.id,
					data: result,
				};
			}
			this.$emit('AddressSearch', res);
			this.searching = false;
			this.doBlur = false;
			this.dirty = false;
			this.autocomplete.autocompleteRef.blur();
		}
	}

	private addressSearchErrorPopUp() {
		const cms = { btnSecondLabel: 'Ok', btnSecondStyle: 'btn-outline-secondary' };
		const modal = modalWithExit(cms);
		modal.title = 'Problem med adressesøgning';
		modal.content =
			'Der er desværre i øjeblikket problemer med adressesøgning. Prøv igen om et øjeblik eller kontakt os.';
		modal.id = 'address search error';
		togglePopup(cms, modal);
	}
}
